import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Input, InputWrapper } from "@amzn/stencil-react-components/input";
import { Col, Hr, Row, View } from "@amzn/stencil-react-components/layout";
import { Table } from "@amzn/stencil-react-components/table";
import { Text } from "@amzn/stencil-react-components/text";
import IconChevronLeftSmall from "@amzn/stencil-react-icons/icons/icon-chevron-left-small";
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  publishSecurityKeyOtpErrorBannerMetric,
  publishSecurityKeyOtpMetrics,
  publishSecurityKeyStepCompleteMetrics,
} from "@/apis/Metrics";
import YubiKeyNanoUsbCPng from "@/assets/images/yubiKeyNanoUsbC.png";
import YubiKeyUsbCPng from "@/assets/images/yubiKeyUsbC.png";
import ZukeyFido2Png from "@/assets/images/zukeyFido2.png";
import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";
import { STEP_ID } from "@/components/constants";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";

import {
  InputTimingData,
  OTPValidationResult,
  validateOTP,
} from "./helpers/otp-validation";
import SecurityKeyEducationalContent from "./SecurityKeyEducationalContent";
import { SecurityKeyTestingSuccessScreen } from "./SecurityKeyTestingSuccessScreen";
import {
  SecurityKeyErrorType,
  SecurityKeyUsbErrorScreen,
} from "./SecurityKeyUsbErrorScreen";

/**
 * Timeout duration for security key usage detection in milliseconds (30 seconds).
 * If users don't interact with their security key within this timeframe,
 * an error banner will be displayed to help troubleshoot the issue.
 */
const SECURITY_KEY_USAGE_TIMEOUT_MS = 30 * 1000;

/**
 * Props for the SecurityKeyOtpInputScreen component.
 */
interface SecurityKeyOtpInputScreenProps {
  /** Callback function to handle navigation back to the previous screen */
  onBack: () => void;
  /** Number of security key detection attempts made so far */
  detectionAttempts: number;
  /** Whether to immediately show detection timeout error state */
  showDetectionTimeoutError?: boolean;
  /** Optional callback for opening the issue reporting modal */
  onReportIssue?: (fromOtpError?: boolean) => void;
  /** Optional callback for skipping security key testing and continuing onboarding */
  onContinueWithoutTesting?: () => void;
}

/**
 * Represents the current state of the OTP validation process.
 *
 * - `idle`: No validation in progress, waiting for user input
 * - `validating`: Currently processing and validating the OTP
 * - `valid`: OTP is valid but from manual input (not security key)
 * - `invalid`: OTP failed validation checks
 * - `loading`: Security key detected, transitioning to success screen
 */
type ValidationState = "idle" | "validating" | "valid" | "invalid" | "loading";

/**
 * Contains the validation state and result information for OTP processing.
 * Tracks both the current validation state and the detailed validation result.
 */
interface ValidationStatus {
  /** Current validation state indicating the processing status */
  state: ValidationState;
  /** Detailed validation result object, null if no validation has occurred yet */
  result: OTPValidationResult | null;
}

/**
 * Screen component for OTP input and validation after security key detection.
 *
 * This component handles the security key testing phase where users interact with
 * their detected security key to generate and validate a one-time password (OTP).
 * It includes comprehensive validation logic, timing analysis, and error handling.
 *
 * Validation Process:
 * 1. User focuses on input field (starts timing)
 * 2. User interacts with security key to generate OTP
 * 3. OTP is validated for format, timing, and input method
 * 4. Success leads to confirmation screen, failure shows error guidance
 *
 * @param props - The component props
 * @param props.onBack - Function to navigate back to previous screen
 * @param props.detectionAttempts - Number of detection attempts for metrics
 * @param props.showDetectionTimeoutError - Whether to show timeout error immediately
 * @param props.onReportIssue - Optional function to open issue reporting
 * @param props.onContinueWithoutTesting - Optional function to skip testing
 * @returns JSX element containing the OTP input screen with validation
 */
export function SecurityKeyOtpInputScreen({
  onBack,
  detectionAttempts,
  showDetectionTimeoutError = false,
  onReportIssue: onReportIssueProp,
  onContinueWithoutTesting: onContinueWithoutTestingProp,
}: SecurityKeyOtpInputScreenProps): JSX.Element {
  const [otpValue, setOtpValue] = useState("");
  const [validation, setValidation] = useState<ValidationStatus>({
    state: "idle",
    result: null,
  });
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [timeoutReached, setTimeoutReached] = useState(false);
  const otpStartTimeRef = useRef<number | null>(null);
  const otpSuccessfulAttemptsRef = useRef(0);

  const { dispatch } = useContext(AppContext);
  const intl = useIntl();

  const inputRef = useRef<HTMLInputElement>(null);
  const startTimeRef = useRef<number>(0);
  const endTimeRef = useRef<number>(0);
  const wasPastedRef = useRef<boolean>(false);
  const validationTimeout = useRef<NodeJS.Timeout>();
  const usageTimeoutRef = useRef<NodeJS.Timeout>();

  /**
   * Used to configure the educational table showing different types of security keys
   * and their specific usage instructions to help users interact correctly.
   */
  interface SecurityKeyData {
    /** Human-readable name of the security key type displayed to users */
    type: string;
    /** Unique identifier used for mapping to images and internal logic */
    id: string;
  }

  const securityKeyData: SecurityKeyData[] = [
    {
      type: "Zukey-FIDO2",
      id: "zukey-fido2",
    },
    {
      type: "YubiKey C Nano",
      id: "yubikey-c-nano",
    },
    {
      type: "YubiKey C",
      id: "yubikey-c",
    },
  ];

  const securityKeyImages: Record<string, string> = {
    "zukey-fido2": ZukeyFido2Png,
    "yubikey-c-nano": YubiKeyNanoUsbCPng,
    "yubikey-c": YubiKeyUsbCPng,
  };

  const keyMap: Record<string, string> = {
    "zukey-fido2": "zukey",
    "yubikey-c-nano": "yubikey-nano",
    "yubikey-c": "yubikey",
  };

  const securityKeyColumns = [
    {
      header: (
        <FormattedMessage id="insertAndTestSecurityKey-otpInput-tableHeader-type" />
      ),
      cellComponent: ({ data }: { data: SecurityKeyData }) => {
        const imageSrc = securityKeyImages[data.id];

        return (
          <Row alignItems="center" columnGap="S200">
            <View
              width="90px"
              height="90px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <img src={imageSrc} alt={data.type} width="100%" height="100%" />
            </View>
            <Text fontWeight="bold">
              <FormattedMessage
                id={`insertAndTestSecurityKey-otpInput-securityKeyType-${
                  keyMap[data.id]
                }`}
              />
            </Text>
          </Row>
        );
      },
    },
    {
      header: (
        <FormattedMessage id="insertAndTestSecurityKey-otpInput-tableHeader-instructions" />
      ),
      cellComponent: ({ data }: { data: SecurityKeyData }) => (
        <FormattedMessage
          id={`insertAndTestSecurityKey-otpInput-securityKeyInstructions-${
            keyMap[data.id]
          }`}
        />
      ),
    },
  ];

  /**
   * Resets the OTP input field and validation state to initial values.
   * Clears any pending validation timeouts and timing tracker data.
   */
  const resetInput = useCallback(() => {
    setOtpValue("");
    setValidation({ state: "idle", result: null });
    startTimeRef.current = 0;
    endTimeRef.current = 0;
    wasPastedRef.current = false;
    if (validationTimeout.current) {
      clearTimeout(validationTimeout.current);
    }
  }, []);

  /**
   * Starts the usage timeout timer for security key interaction detection.
   *
   * Sets a timer that will trigger an error banner if the user doesn't interact
   * with their security key within the timeout period. This helps detect when
   * users may be having trouble using their security key or if the key is not
   * functioning properly.
   */
  const startUsageTimeout = useCallback(() => {
    if (usageTimeoutRef.current) {
      clearTimeout(usageTimeoutRef.current);
    }

    usageTimeoutRef.current = setTimeout(() => {
      publishSecurityKeyOtpErrorBannerMetric();
      setTimeoutReached(true);
    }, SECURITY_KEY_USAGE_TIMEOUT_MS);
  }, []);

  /**
   * Clears the usage timeout timer to prevent error banner from showing.
   *
   * This function is called when the user successfully validates their security key
   * or when they choose to continue without testing, preventing the timeout error
   * from triggering unnecessarily.
   */
  const clearUsageTimeout = useCallback(() => {
    if (usageTimeoutRef.current) {
      clearTimeout(usageTimeoutRef.current);
      usageTimeoutRef.current = undefined;
    }
  }, []);

  /**
   * Handles continuing without testing the security key.
   * Skips the security key validation step and proceeds to next step.
   */
  const handleContinueWithoutTesting = useCallback(() => {
    clearUsageTimeout();
    if (onContinueWithoutTestingProp) {
      onContinueWithoutTestingProp();
    } else {
      dispatch({
        type: ActionType.NEXT_STEP,
        stepId: STEP_ID.INSERT_AND_TEST_SECURITY_KEY,
      });
    }
  }, [clearUsageTimeout, onContinueWithoutTestingProp]);

  /**
   * Handles reporting security key issues by opening the issue reporting modal.
   *
   * Clears any active usage timeouts and delegates to the provided callback
   * function to open the issue reporting interface. This allows users to
   * report problems with their security key and request assistance.
   */
  const handleReportIssue = useCallback(() => {
    clearUsageTimeout();
    if (onReportIssueProp) {
      const isFromOtpError = showDetectionTimeoutError || timeoutReached;
      onReportIssueProp(isFromOtpError);
    }
  }, [
    clearUsageTimeout,
    onReportIssueProp,
    showDetectionTimeoutError,
    timeoutReached,
  ]);

  /**
   * Handles input focus events to start timing for OTP attempts.
   */
  const handleInputFocus = useCallback(() => {
    if (otpStartTimeRef.current === null) {
      otpStartTimeRef.current = performance.now();
    }
  }, []);

  const handleSuccessNext = useCallback(() => {
    publishSecurityKeyStepCompleteMetrics({
      detectionAttempts,
      otpAttempts: otpSuccessfulAttemptsRef.current,
    });

    dispatch({
      type: ActionType.NEXT_STEP,
      stepId: STEP_ID.INSERT_AND_TEST_SECURITY_KEY,
    });
  }, [detectionAttempts]);

  /**
   * Validates OTP input with timing analysis to detect hardware security keys.
   *
   * Performs validation after a delay to allow complete input, then processes
   * the result based on whether it appears to come from a security key or
   * manual entry.
   *
   * @param value the OTP string to validate
   */
  const validateOTPInput = useCallback(
    (value: string) => {
      if (!value.trim()) {
        resetInput();
        return;
      }

      setValidation((prev) => ({ ...prev, state: "validating" }));

      if (validationTimeout.current) {
        clearTimeout(validationTimeout.current);
      }

      validationTimeout.current = setTimeout(() => {
        const timingData: InputTimingData = {
          startTime: startTimeRef.current,
          endTime: endTimeRef.current || Date.now(),
          wasPasted: wasPastedRef.current,
        };
        const result = validateOTP(value, timingData);

        if (result.isValid && result.source === "security_key_detected") {
          clearUsageTimeout();
          otpSuccessfulAttemptsRef.current += 1;

          if (otpStartTimeRef.current !== null) {
            const elapsedTime = performance.now() - otpStartTimeRef.current;
            publishSecurityKeyOtpMetrics({
              elapsedTime,
              attemptNumber: otpSuccessfulAttemptsRef.current,
            });
          }

          setValidation({
            state: "loading",
            result: result,
          });

          setTimeout(() => {
            setShowSuccessScreen(true);
          }, 500);
        } else {
          setValidation({
            state: result.isValid ? "valid" : "invalid",
            result: result,
          });
        }
      }, 200);
    },
    [resetInput, clearUsageTimeout]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement> | CustomEvent<{ value: string }>) => {
      let value = "";

      if ("target" in event && event.target) {
        value = (event.target as HTMLInputElement).value || "";
      } else if ("detail" in event && event.detail) {
        value = (event as CustomEvent<{ value: string }>).detail.value || "";
      }

      setOtpValue(value);
      if (startTimeRef.current === 0) {
        startTimeRef.current = Date.now();
      }
      endTimeRef.current = Date.now();
      validateOTPInput(value);
    },
    [validateOTPInput]
  );

  const handlePaste = useCallback(() => {
    wasPastedRef.current = true;
  }, []);

  useEffect(() => {
    if (inputRef.current && !showDetectionTimeoutError) {
      inputRef.current.focus();
    }

    // Publish metric if showing detection timeout error
    if (showDetectionTimeoutError) {
      publishSecurityKeyOtpErrorBannerMetric();
    }

    // Start the usage timeout when component mounts (skip if showing detection timeout error)
    if (!showDetectionTimeoutError) {
      startUsageTimeout();
    }

    return () => {
      if (validationTimeout.current) {
        clearTimeout(validationTimeout.current);
      }
      if (usageTimeoutRef.current) {
        clearTimeout(usageTimeoutRef.current);
      }
    };
  }, [startUsageTimeout, showDetectionTimeoutError]);

  const showErrorScreen = showDetectionTimeoutError || timeoutReached;
  const errorType: SecurityKeyErrorType = showDetectionTimeoutError
    ? "usb_detection_timeout"
    : "non_functional_security_key";

  if (showSuccessScreen) {
    return <SecurityKeyTestingSuccessScreen onNext={handleSuccessNext} />;
  }

  return (
    <Col rowGap="S400" dataTestId="securityKeyTestingScreen">
      <Row justifyContent="flex-start">
        <Button
          onClick={onBack}
          variant={ButtonVariant.Tertiary}
          icon={<IconChevronLeftSmall />}
          dataTestId="securityKeyDetectedBackButton"
        >
          <FormattedMessage id="button-back" />
        </Button>
      </Row>
      {showDetectionTimeoutError && <SecurityKeyEducationalContent />}
      <StepBodyStyledText>
        <Col rowGap="S400">
          {showDetectionTimeoutError && <Hr color="neutral20" size="narrow" />}
          {!showDetectionTimeoutError && (
            <>
              <Text
                fontSize="T300"
                fontWeight="bold"
                dataTestId="securityKeyTestingHeading"
              >
                <FormattedMessage id="insertAndTestSecurityKey-otpInput-testingHeading" />
              </Text>
              <Text fontWeight="regular" dataTestId="securityKeyInstructions">
                <FormattedMessage id="insertAndTestSecurityKey-otpInput-instructions" />
              </Text>
              <Col rowGap="S200">
                <Table
                  data={securityKeyData}
                  columns={securityKeyColumns}
                  dataTestId="securityKeyTypesTable"
                />
              </Col>
              <Col rowGap="S200">
                <View maxWidth="400px" width="100%">
                  <Col rowGap="S100">
                    <InputWrapper
                      id="otpInput"
                      labelText={intl.formatMessage({
                        id: "insertAndTestSecurityKey-otpInput-otpLabel",
                      })}
                      dataTestId="otpInputWrapper"
                    >
                      {({ id, ...inputProps }) => (
                        <Input
                          {...inputProps}
                          ref={inputRef}
                          id={id}
                          type="password"
                          autoComplete="off"
                          value={otpValue}
                          onChange={handleInputChange}
                          onFocus={handleInputFocus}
                          onPaste={handlePaste}
                          error={validation.state === "invalid"}
                          warning={
                            validation.state === "validating" ||
                            validation.state === "loading"
                          }
                          disabled={validation.state === "loading"}
                          dataTestId="otpInput"
                        />
                      )}
                    </InputWrapper>
                  </Col>
                </View>
              </Col>
            </>
          )}
          {(showDetectionTimeoutError || showErrorScreen) && (
            <SecurityKeyUsbErrorScreen
              onReportIssue={handleReportIssue}
              onContinueWithoutTesting={handleContinueWithoutTesting}
              errorType={errorType}
            />
          )}
        </Col>
      </StepBodyStyledText>
    </Col>
  );
}
